import { ConsentStatus } from "@/api/dtos";
import { getEConsentFormRegistration } from "@/components/consent/eConsentFormRegistry";
import { OnlinePatientManagementContext } from "@ngt/opms";
import { useContext, useMemo } from "react";
import useSWR from "swr";

export interface PatientEConsentDisplayContext {
    formDefinitionCode?: string;
    formDefinitionName?: string;
    participantName?: string;
    institutionCode?: string;
    institutionName?: string;
    principalInvestigator?: string;
    picfDownloadUrl?: string;
    picfVersion?: string;
    readOnly: boolean;
    consentStatus?: ConsentStatus;
}

export interface PatientEConsentResponse<TForm = Record<string, unknown>> {
    form?: TForm;
    context?: PatientEConsentDisplayContext;
    responseStatus?: unknown;
}

export const usePatientEConsentForm = <TForm,>(formCode?: string, guid?: string) => {
    const { serviceStackClient, dtos } = useContext(OnlinePatientManagementContext);
    const registration = getEConsentFormRegistration(formCode);
    const formTypeName = registration?.requestTypeName;
    const requestType = useMemo(
        () => formTypeName ? dtos[`${formTypeName}EConsentFormGet`] : undefined,
        [dtos, formTypeName]
    );
    const key = requestType && guid ? ["patient-econsent", formTypeName, guid] : null;
    const response = useSWR<PatientEConsentResponse<TForm>>(
        key,
        async (): Promise<PatientEConsentResponse<TForm>> =>
            await serviceStackClient.get(new requestType({ guid })) as PatientEConsentResponse<TForm>
    );

    return {
        ...response,
        registration,
        formTypeName,
        loading: !!key && !response.data && !response.error
    };
};

export default usePatientEConsentForm;
